import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import CardClient from '@atlaskit/link-provider/client';

import {
	NEW_WHITEBOARD_QUERY_PARAM,
	PAYWALL_DEACTIVATION_IDENTIFIER,
	TEMPLATE_PICKER_QUERY_PARAM,
} from '@confluence/whiteboard-utils/entry-points/whiteboardConstants.ts';
import {
	WHITEBOARD_BOARD,
	DATABASE_VIEW,
	EMBED_VIEW,
	FOLDER_VIEW,
} from '@confluence/named-routes/entry-points/routes.ts';
import {
	fetchDataFromORS,
	IS_NEW_EMBED_QUERY_PARAM_KEY,
	normalizeAndValidateUrl,
} from '@confluence/embed-utils';
import {
	IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME,
	IN_EDITOR_TEMPLATES_QUERY_OPEN,
	CONTEXT_PANEL_TAB_TARGET,
	CONTEXT_PANEL_TAB_TARGET_IMPORT,
} from '@confluence/template-utils/entry-points/urlUtils.ts';
import { mutateInterop } from '@confluence/apollo-relay-interop/entry-points/mutate.ts';
import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { appendQueryParams, processDraftUrl, getEditPageUrl } from '../helpers.ts';
import { CreateContentMutation } from '../queries/CreateContentMutation.graphql.ts';
import { CreateWhiteboardMutation } from '../queries/CreateWhiteboard.graphql.ts';
import { CreateDatabaseMutation } from '../queries/CreateDatabase.graphql.ts';
import { CreateSmartLinkEmbedMutation } from '../queries/CreateSmartLinkEmbed.graphql.ts';
import { CreateFolderMutation } from '../queries/CreateFolder.graphql.ts';
import type {
	CreateContentMutationVariables,
	ContentSpecificCreateInput,
} from '../queries/__types__/CreateContentMutation.ts';
import {
	GraphQLContentStatus as PageGraphQLContentStatus,
	ConfluencePageSubType,
} from '../queries/__types__/CreateContentMutation.ts';
import type { ContentSpecificCreateInput as CreateWhiteboardSpecificInput } from '../queries/__types__/CreateWhiteboardMutation.ts';
import type { CreateDatabaseMutationVariables } from '../queries/__types__/CreateDatabaseMutation.ts';
import type {
	ContentSpecificCreateInput as SmartLinkContentSpecificCreateInput,
	CreateSmartLinkEmbedMutationVariables,
} from '../queries/__types__/CreateSmartLinkEmbedMutation.ts';
import type { CreateFolderMutationVariables } from '../queries/__types__/CreateFolderMutation.ts';
import { GraphQLContentStatus } from '../queries/__types__/CreateSmartLinkEmbedMutation.ts';
import type {
	CreateWhiteboardMutation$data,
	CreateWhiteboardMutation$variables,
} from '../queries/__generated__/CreateWhiteboardMutation.graphql.ts';
import type {
	CreateDatabaseMutation$data,
	CreateDatabaseMutation$variables,
} from '../queries/__generated__/CreateDatabaseMutation.graphql.ts';
import type {
	CreateSmartLinkEmbedMutation$data,
	CreateSmartLinkEmbedMutation$variables,
} from '../queries/__generated__/CreateSmartLinkEmbedMutation.graphql.ts';
import type {
	CreateFolderMutation$data,
	CreateFolderMutation$variables,
} from '../queries/__generated__/CreateFolderMutation.graphql.ts';
import type {
	CreateContentMutation$data,
	CreateContentMutation$variables,
} from '../queries/__generated__/CreateContentMutation.graphql.ts';
import type { PaywallStatus } from '../createContentInputTypes.ts';
const NEW_DATABASE_QUERY_PARAM = 'isNewDatabase';
export const MAX_SMART_LINK_EMBED_TITLE_LENGTH = 60;

export type CreateContentResponse = {
	analyticsAttributes?: Record<string, any>;
	nonPrivacySafeAttributes?: Record<string, any>;
	contentId?: string | null;
	spaceId?: string | null;
	url?: string;
	collabService?: string | null;
	returnedSubType?: string | null;
	returnedStatus?: string | null;
};

export type createPageContentParams = {
	subType?: ConfluencePageSubType;
	spaceKey: string;
	parentPage?: string;
	title?: string;
	draftId?: string;
	linkCreation?: string;
	fabricEditorModeCacheStoreActions?: any;
	templateKey?: string;
	shouldOpenTemplatesGallery?: boolean;
	shouldOpenImportTab?: boolean;
	type: string;
};

export type createDatabaseParams = {
	spaceKey: string;
	parentId?: string;
};

export type createSmartLinkEmbedParams = {
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	createSource?: string;
	spaceKey?: string;
	parentId?: string;
	status?: string;
	embedUrl?: string;
};

export type createFolderParams = {
	spaceKey?: string;
	parentId?: string;
};

const _createWhiteboardMutation = async (
	variables: CreateWhiteboardMutationVariables,
): Promise<CreateWhiteboardMutation$data> => {
	const {
		spaceKey,
		parentId,
		paywallStatus,
		deactivationIdentifier,
		restoreVersionContentId,
		title,
	} = variables;

	const contentSpecificCreateInput: CreateWhiteboardSpecificInput[] = [];

	if (paywallStatus) {
		contentSpecificCreateInput.push({
			key: 'defaultPaywallStatus',
			value: paywallStatus,
		});
	}

	if (deactivationIdentifier) {
		contentSpecificCreateInput.push({
			key: 'deactivationIdentifier',
			value: deactivationIdentifier,
		});
	}

	if (restoreVersionContentId) {
		contentSpecificCreateInput.push({
			key: 'restoreVersionContentId',
			value: restoreVersionContentId,
		});
	}

	const result = await mutateInterop<
		CreateWhiteboardMutation$data,
		CreateWhiteboardMutation$variables
	>({
		mutation: CreateWhiteboardMutation,
		variables: {
			spaceKey,
			parentId,
			contentSpecificCreateInput,
			title,
		},
	});
	return result.data!;
};
type CreateWhiteboardMutationVariables = {
	spaceKey: string;
	parentId?: string | null;
	deactivationIdentifier?: string;
	paywallStatus?: PaywallStatus;
	restoreVersionContentId?: string;
	title?: string;
};

export const createWhiteboardContent = async (
	mutationVariables: CreateWhiteboardMutationVariables,
	urlParams: object = {},
): Promise<CreateContentResponse> => {
	const result = await _createWhiteboardMutation(mutationVariables);
	const space = result.createContentContextual?.space;
	const contentId = result.createContentContextual?.id;

	const { deactivationIdentifier, restoreVersionContentId } = mutationVariables || {};

	const url = WHITEBOARD_BOARD.toUrl(
		{
			spaceKey: space?.alias || space?.key,
			contentId,
		},
		{
			query: {
				[NEW_WHITEBOARD_QUERY_PARAM]: true,
				[TEMPLATE_PICKER_QUERY_PARAM]: true,
				...urlParams,
				...(deactivationIdentifier && {
					[PAYWALL_DEACTIVATION_IDENTIFIER]: deactivationIdentifier,
				}),
				...(restoreVersionContentId && {
					flow: 'restoreVersion',
				}),
			},
		},
	);
	return {
		contentId,
		spaceId: space?.id,
		url,
		collabService: 'canvas-relay',
	};
};

const _createDatabaseMutation = async (
	variables: CreateDatabaseMutationVariables,
): Promise<CreateDatabaseMutation$data> => {
	const result = await mutateInterop<CreateDatabaseMutation$data, CreateDatabaseMutation$variables>(
		{
			mutation: CreateDatabaseMutation,
			variables,
		},
	);
	return result.data!;
};

export const createDatabaseContent = async ({
	spaceKey,
	parentId,
}: createDatabaseParams): Promise<CreateContentResponse> => {
	const result = await _createDatabaseMutation({ spaceKey, parentId });
	const space = result.createContentContextual?.space;
	const contentId = result.createContentContextual?.id;

	const url = DATABASE_VIEW.toUrl(
		{
			spaceKey: space?.alias || space?.key,
			contentId,
		},
		{
			query: {
				[NEW_DATABASE_QUERY_PARAM]: true,
			},
		},
	);
	return {
		contentId,
		spaceId: space?.id,
		url,
		collabService: 'canvas-relay',
	};
};

const _CreateSmartLinkEmbedMutation = async (
	variables: CreateSmartLinkEmbedMutationVariables,
): Promise<CreateSmartLinkEmbedMutation$data> => {
	const result = await mutateInterop<
		CreateSmartLinkEmbedMutation$data,
		CreateSmartLinkEmbedMutation$variables
	>({
		mutation: CreateSmartLinkEmbedMutation,
		variables,
	});
	return result.data!;
};

export const createSmartLinkEmbedContent = async ({
	createAnalyticsEvent,
	createSource,
	spaceKey,
	parentId,
	embedUrl,
}: createSmartLinkEmbedParams): Promise<CreateContentResponse> => {
	let contentSpecificCreateInput: SmartLinkContentSpecificCreateInput[] | undefined;
	let product: string | undefined;
	let title: string | undefined;
	let embedExtensionKey: string | null | undefined;
	let embedResourceType: string | null | undefined;
	let normalizedEmbedUrl: string | null | undefined;

	if (embedUrl) {
		const cardClient = new CardClient();
		cardClient.setProduct('CONFLUENCE');
		normalizedEmbedUrl = normalizeAndValidateUrl(embedUrl);

		if (!normalizedEmbedUrl) {
			throw new Error('Invalid smart link embed URL');
		}

		const { meta, parsedEmbedData } = await fetchDataFromORS({
			cardClient,
			createAnalyticsEvent,
			embedUrl: normalizedEmbedUrl,
		});

		title =
			parsedEmbedData.title &&
			parsedEmbedData.title.length > MAX_SMART_LINK_EMBED_TITLE_LENGTH &&
			fg('cc_drag_and_drop_smart_link_from_content_to_tree')
				? `${parsedEmbedData.title.slice(0, MAX_SMART_LINK_EMBED_TITLE_LENGTH - 3)}...`
				: parsedEmbedData.title;
		product = parsedEmbedData.product || undefined;
		embedExtensionKey = meta?.extensionKey ?? null;
		embedResourceType = meta?.resourceType ?? null;

		contentSpecificCreateInput = [];

		if (parsedEmbedData.iconURL) {
			contentSpecificCreateInput.push({
				key: 'embedIconUrl',
				value: parsedEmbedData.iconURL,
			});
		}

		// We should use the URL returned by ORS if possible since it will return the URL in
		// the most normalized form (e.g. it can reformat URLs that contain unicode characters
		// such that BE will accept them)
		contentSpecificCreateInput.push({
			key: 'embedUrl',
			value: parsedEmbedData.url ?? normalizedEmbedUrl,
		});

		if (product) {
			contentSpecificCreateInput.push({ key: 'product', value: product });
		}

		if (createSource) {
			contentSpecificCreateInput.push({
				key: 'createSource',
				value: createSource,
			});
		}
	}

	// smart link embeds go through blank creation flow so no initial URL is provided to the mutation
	const smartLinkResult = await _CreateSmartLinkEmbedMutation({
		...(contentSpecificCreateInput ? { contentSpecificCreateInput } : {}),
		...(title ? { title } : {}),
		type: 'embed',
		status: GraphQLContentStatus.CURRENT,
		parentId,
		spaceKey,
	});
	const space = smartLinkResult.createContentContextual?.space;
	const contentId = smartLinkResult.createContentContextual?.id;

	const query = {};

	// This query paramter is used to display the dot grid background when new *blank* embeds
	// are created. If there's an embedUrl already provided, it's not a blank embed.
	if (!embedUrl) {
		query[IS_NEW_EMBED_QUERY_PARAM_KEY] = true;
	}

	const url = EMBED_VIEW.toUrl(
		{
			spaceKey: space?.alias || space?.key,
			contentId,
		},
		{ query },
	);

	return {
		analyticsAttributes: {
			extensionKey: embedExtensionKey,
			destinationObjectType: embedResourceType,
		},
		nonPrivacySafeAttributes: {
			linkDomain: product,
			url: normalizedEmbedUrl,
		},
		collabService: 'no-collab-for-smart-link-embeds',
		contentId,
		spaceId: space?.id,
		url,
	};
};

const _CreateFolderMutation = async (
	variables: CreateFolderMutationVariables,
): Promise<CreateFolderMutation$data> => {
	const result = await mutateInterop<CreateFolderMutation$data, CreateFolderMutation$variables>({
		mutation: CreateFolderMutation,
		variables,
	});
	return result.data!;
};

export const createFolderContent = async ({
	spaceKey,
	parentId,
}: createFolderParams): Promise<CreateContentResponse> => {
	const folderResult = await _CreateFolderMutation({
		type: 'folder',
		status: GraphQLContentStatus.CURRENT,
		parentId,
		spaceKey,
	});
	const space = folderResult.createContentContextual?.space;
	const contentId = folderResult.createContentContextual?.id;

	const folderViewUrl =
		spaceKey && contentId && expVal('confluence_folder_view', 'isEnabled', false)
			? FOLDER_VIEW.toUrl({
					spaceKey,
					contentId,
				})
			: undefined;

	return {
		contentId,
		spaceId: space?.id,
		url: folderViewUrl ? appendQueryParams(folderViewUrl, { inlineTitleEdit: 'true' }) : undefined,
		collabService: 'no-collab-for-folders',
	};
};

const _createBlankContentMutation = async (
	variables: CreateContentMutationVariables,
): Promise<CreateContentMutation$data> => {
	const result = await mutateInterop<CreateContentMutation$data, CreateContentMutation$variables>({
		mutation: CreateContentMutation,
		variables,
	});
	return result.data!;
};

const createContentGQL = async ({
	subType,
	spaceKey,
	parentPage,
	title,
	linkCreation,
	fabricEditorModeCacheStoreActions,
	templateKey,
	shouldOpenTemplatesGallery,
	shouldOpenImportTab,
	type,
}: createPageContentParams): Promise<CreateContentResponse> => {
	const status =
		subType === ConfluencePageSubType.LIVE
			? PageGraphQLContentStatus.CURRENT
			: PageGraphQLContentStatus.DRAFT;

	const contentSpecificCreateInput: ContentSpecificCreateInput[] | undefined = templateKey
		? getTemplateContentSpecificInput(templateKey)
		: undefined;

	const variables: CreateContentMutationVariables = {
		subType,
		spaceKey,
		parentId: parentPage,
		title,
		status,
		contentSpecificCreateInput,
		type,
	};

	const pageData = await _createBlankContentMutation(variables);
	const contentId = pageData.createContentContextual?.id;
	const spaceId = pageData.createContentContextual?.space?.id;
	const editui = pageData.createContentContextual?.links?.editui || '';
	const collabService = pageData.createContentContextual?.metadata?.frontend?.collabService;
	const returnedSubType = pageData.createContentContextual?.subType;
	const returnedSpaceKey =
		pageData.createContentContextual?.space?.alias || pageData.createContentContextual?.space?.key;
	const context = pageData.createContentContextual?.links?.context || '';

	let url =
		subType === ConfluencePageSubType.LIVE
			? processDraftUrl(editui)
			: getEditPageUrl({
					spaceKey: returnedSpaceKey,
					contentId,
					url: context + editui,
					type,
				});

	if (linkCreation) {
		url = appendQueryParams(
			url,
			{ linkCreation },
			fabricEditorModeCacheStoreActions?.setFabricEditorMode,
		);
	}

	if (shouldOpenTemplatesGallery) {
		url = appendQueryParams(
			url,
			{ [IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME]: IN_EDITOR_TEMPLATES_QUERY_OPEN },
			fabricEditorModeCacheStoreActions?.setFabricEditorMode,
		);
	}

	if (shouldOpenImportTab) {
		url = appendQueryParams(
			url,
			{
				[IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME]: IN_EDITOR_TEMPLATES_QUERY_OPEN,
				[CONTEXT_PANEL_TAB_TARGET]: CONTEXT_PANEL_TAB_TARGET_IMPORT,
			},
			fabricEditorModeCacheStoreActions?.setFabricEditorMode,
		);
	}

	return { contentId, spaceId, url, collabService, returnedSubType, returnedStatus: status };
};

export const createPageContent = async (
	args: createPageContentParams,
): Promise<CreateContentResponse> => {
	return createContentGQL({ ...args, type: 'page' });
};

export const createBlogContent = async (spaceKey: string): Promise<CreateContentResponse> => {
	return createContentGQL({ spaceKey, type: 'blogpost' });
};

// Template configuration mapping
const TEMPLATE_CONFIG = {
	'meeting-notes-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
	'projectplan-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
	'requirements-blueprint': {
		key: 'templateId',
		plugin: 'confluence-software-blueprints',
	},
	'marketing-campaign-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
	'marketing-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
	'design-decision-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
	'standard-operating-procedure-sop-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
	'business-status-update-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
	'kb-how-to-article-blueprint': {
		key: 'templateId',
		plugin: 'confluence-knowledge-base',
	},
	'fundraising-database-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
	'employee-handbook-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
	'hiring-process-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
	'business-strategy-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
	'account-planning-for-sales-blueprint': {
		key: 'templateId',
		plugin: 'confluence-business-blueprints',
	},
} as const;

const TEMPLATE_REGEX =
	/^com\.atlassian\.confluence\.plugins\.confluence(-[0-9a-z]+)(-[0-9a-z]+)*-blueprints:([0-9a-z]+)(-[0-9a-z]+)*-blueprint$/;

/**
 * Generates the contentSpecificCreateInput for a given template key
 * @param templateKey - The template key (e.g., 'meeting-notes-blueprint')
 * @returns ContentSpecificCreateInput array or undefined if template not found
 */
const getTemplateContentSpecificInput = (
	templateKey: string,
): ContentSpecificCreateInput[] | undefined => {
	const config = TEMPLATE_CONFIG[templateKey as keyof typeof TEMPLATE_CONFIG];

	if (!config) {
		// Support full template keys
		if (TEMPLATE_REGEX.test(templateKey)) {
			return [
				{
					key: 'templateId',
					value: templateKey,
				},
			];
		}

		// Return undefined for unknown templates
		return undefined;
	}

	return [
		{
			key: config.key,
			value: `com.atlassian.confluence.plugins.${config.plugin}:${templateKey}`,
		},
	];
};
